import { z } from 'zod'
import type { LLMConfig, MemoryConfig, AgentSettings } from './agent'

// Template categories
export const TEMPLATE_CATEGORIES = {
  all: 'Todos',
  support: 'Suporte',
  productivity: 'Produtividade',
  content: 'Conteúdo',
  development: 'Desenvolvimento',
  research: 'Pesquisa',
} as const

export type TemplateCategory = keyof typeof TEMPLATE_CATEGORIES

// Agent Template list item (lightweight)
export interface AgentTemplateListItem {
  id: string
  slug: string
  name: string
  description: string | null
  icon: string
  category: string
  display_order: number
}

// Full Agent Template
export interface AgentTemplate {
  id: string
  slug: string
  name: string
  description: string | null
  icon: string
  category: string
  system_prompt: string
  llm_config: LLMConfig
  memory_config: MemoryConfig
  settings: AgentSettings
  suggested_tools: string[]
  is_active: boolean
  display_order: number
  created_at: string
  updated_at: string
}

// API Response types
export interface AgentTemplateListResponse {
  templates: AgentTemplateListItem[]
  total: number
}

// Request types
export interface CreateAgentFromTemplateRequest {
  template_id: string
  name: string
  description?: string | null
  llm_config?: Partial<LLMConfig>
  memory_config?: Partial<MemoryConfig>
  settings?: Partial<AgentSettings>
}

// Zod schema for creating agent from template
export const createAgentFromTemplateSchema = z.object({
  template_id: z.string().uuid('ID do template inválido'),
  name: z
    .string()
    .min(2, 'Nome deve ter no mínimo 2 caracteres')
    .max(255, 'Nome deve ter no máximo 255 caracteres'),
  description: z
    .string()
    .max(2000, 'Descrição deve ter no máximo 2000 caracteres')
    .optional()
    .nullable(),
})

export type CreateAgentFromTemplateData = z.infer<typeof createAgentFromTemplateSchema>

// Helper to get category label
export function getCategoryLabel(category: string): string {
  return TEMPLATE_CATEGORIES[category as TemplateCategory] || category
}

// Helper to get category color class
export function getCategoryColorClass(category: string): string {
  const colors: Record<string, string> = {
    support: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    productivity: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    content: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    development: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    research: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
  }
  return colors[category] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
}
